import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getContentTypeFromUrl } from "@/utils/getContentTypeFromUrl";

interface ExploreItemData {
  id: string;
  title: string;
  url: string | null;
  coverUrl?: string | null;
}

export function useImportExploreContent() {
  const [importing, setImporting] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const navigate = useNavigate();

  const importContent = async (item: ExploreItemData) => {
    if (!item.url) {
      toast.error("This content has no URL");
      return;
    }

    setImporting(true);
    setImportingId(item.id);

    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        toast.error("Please sign in to save content");
        navigate("/sign-in");
        return;
      }

      // Check if this explore content has already been imported by this user
      const { data: existingContent } = await supabase
        .from("content")
        .select("id")
        .eq("user_id", user.id)
        .eq("source_url", item.url)
        .maybeSingle();

      if (existingContent) {
        // Content already exists, just navigate to it
        navigate(`/content/${existingContent.id}`);
        return;
      }

      // Determine content type from URL
      const contentType = getContentTypeFromUrl(item.url);

      const isMedia = ["video", "audio", "youtube"].includes(contentType);
      const isDocument = ["pdf", "doc", "ppt", "web", "text"].includes(contentType);

      // Insert new content record
      const { data: newContent, error: insertError } = await supabase
        .from("content")
        .insert({
          user_id: user.id,
          title: item.title,
          type: contentType,
          source_url: item.url,
          thumbnail_url: item.coverUrl || null,
          visibility: "private",
          conversion_status: isMedia ? "not_needed" : "pending",
          transcript_status: isMedia ? "pending" : "not_needed",
          text_extraction_status: isDocument ? "pending" : "not_needed",
          chapters_status: "pending",
        })
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      // Trigger background processing based on content type
      if (contentType === "youtube") {
        supabase.functions.invoke("extract-media-transcript", {
          body: { contentId: newContent.id },
        }).catch(err => console.error("Transcript extraction failed:", err));
      } else if (contentType === "web" || contentType === "pdf" || contentType === "doc") {
        supabase.functions.invoke("convert-content-to-pdf", {
          body: { contentId: newContent.id, type: contentType, sourceUrl: item.url },
        }).catch(err => console.error("PDF conversion failed:", err));
      } else if (contentType === "audio" || contentType === "video") {
        supabase.functions.invoke("extract-media-transcript", {
          body: { contentId: newContent.id },
        }).catch(err => console.error("Transcript extraction failed:", err));
      }

      // Trigger refresh of content sections
      window.dispatchEvent(new CustomEvent("content-uploaded"));

      toast.success("Content added to your library");

      // Navigate to the content page
      navigate(`/content/${newContent.id}`);
    } catch (error) {
      console.error("Failed to import explore content:", error);
      toast.error("Failed to add content to your library");
    } finally {
      setImporting(false);
      setImportingId(null);
    }
  };

  return { importContent, importing, importingId };
}
